import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { toast } from "react-toastify";
import { isAuthenticated } from "../../../utils/authSession";
import { isBrowserPhoneChannelEnabled, isCallingEnabled } from "../../../utils/orgFeatures";
import { isDesktopApp } from "../../../utils/isDesktop";
import {
  getBrowserPhoneConfig,
  getTelephonyCalls,
  notifyCallEnded,
  notifyCallFailed,
  notifyCallStarted,
  registerBrowserEvent,
  uploadCallRecording,
  syncCallRecording,
} from "./telephonyApi";
import { fetchGrandstreamRecording } from "./grandstreamRecording";
import { createSipClient } from "./sipClientFactory";
import { subscribeDesktopIncomingPopupActions } from "../../../utils/desktopBridge";
import { BrowserPhoneAction, BrowserPhoneState } from "./telephonyTypes";
import { browserPhoneInitialState, browserPhoneReducer } from "./browserPhoneState";
import { extractCallerFromInvitation } from "./incomingCallUtils";
import {
  resolveInboundFailureReason,
  resolveInboundTerminateStatus,
} from "./callTerminateUtils";
import {
  startIncomingCallAlert,
  stopIncomingCallAlert,
} from "./incomingCallAlerts";
import { isActiveCallState } from "./browserPhoneCallStates";
import { isSdkSuccessResult } from "./linkusSdkResult";
import { disabledBrowserPhoneConfig } from "./browserPhoneConfigUtils";

const extractCaller = (invitation) => extractCallerFromInvitation(invitation);

const extractSipCallId = (invitation) => {
  const id = invitation?.id ?? invitation?.request?.callId;
  return id ? String(id) : null;
};

const extractCallLogId = (response) => response?.id ?? response?.callLogId ?? null;

export const useBrowserPhone = () => {
  const [store, dispatch] = useReducer(browserPhoneReducer, browserPhoneInitialState);
  const sipClientRef = useRef(null);
  const callStartRef = useRef(null);
  const callMetaRef = useRef({});
  const remoteAudioRef = useRef(null);
  const connectPromiseRef = useRef(null);
  const hangupReasonRef = useRef("completed");
  const failureDetailRef = useRef(null);
  const answeredRef = useRef(false);
  const configRef = useRef(null);
  const phoneStateRef = useRef(browserPhoneInitialState.state);
  const loadConfigPromiseRef = useRef(null);

  const releaseSipClient = useCallback(async () => {
    const client = sipClientRef.current;
    sipClientRef.current = null;
    if (client) {
      await client.disconnect().catch(() => undefined);
    }
  }, []);

  phoneStateRef.current = store.state;

  const setRemoteAudioElement = useCallback((element) => {
    remoteAudioRef.current = element;
    if (element && sipClientRef.current) {
      sipClientRef.current.updateHandlers({ remoteAudio: element });
    }
  }, []);

  const mergeCallLogFromResponse = useCallback((response, phoneNumber) => {
    const callLogId = extractCallLogId(response);
    if (!callLogId) {
      return;
    }
    const accountNo =
      response?.accountId && response.accountId !== "UNKNOWN" ? response.accountId : null;
    callMetaRef.current = {
      ...callMetaRef.current,
      phoneNumber: phoneNumber || callMetaRef.current.phoneNumber,
      callLogId,
      accountNo: accountNo || callMetaRef.current.accountNo,
    };
    dispatch({
      type: BrowserPhoneAction.SET_CALL,
      payload: {
        phoneNumber: callMetaRef.current.phoneNumber,
        direction: callMetaRef.current.direction || "INBOUND",
        callLogId,
        accountNo: callMetaRef.current.accountNo,
        remoteIdentity: callMetaRef.current.remoteIdentity,
      },
    });
  }, []);

  const refreshHistory = useCallback(async () => {
    const calls = await getTelephonyCalls();
    dispatch({ type: BrowserPhoneAction.SET_HISTORY, payload: Array.isArray(calls) ? calls : [] });
  }, []);

  const recordCallLogResponse = useCallback(
    (response, phoneNumber) => {
      mergeCallLogFromResponse(response, phoneNumber);
      refreshHistory().catch(() => undefined);
    },
    [mergeCallLogFromResponse, refreshHistory]
  );

  const notifyIncomingRinging = useCallback(
    (phoneNumber, sipCallId) => {
      notifyCallStarted({
        phoneNumber,
        direction: "INBOUND",
        status: "RINGING",
        sipCallId,
      })
        .then((response) => recordCallLogResponse(response, phoneNumber))
        .catch(() => undefined);
    },
    [recordCallLogResponse]
  );

  const invalidateConfigCache = useCallback(() => {
    configRef.current = null;
    loadConfigPromiseRef.current = null;
  }, []);

  const loadConfig = useCallback(async (options = {}) => {
    if (options.forceReload) {
      invalidateConfigCache();
    }

    if (!isCallingEnabled()) {
      const disabledConfig = disabledBrowserPhoneConfig("CALLING_DISABLED");
      configRef.current = disabledConfig;
      dispatch({ type: BrowserPhoneAction.SET_CONFIG, payload: disabledConfig });
      dispatch({ type: BrowserPhoneAction.SET_ERROR, payload: null });
      dispatch({
        type: BrowserPhoneAction.SET_STATE,
        payload: BrowserPhoneState.REGISTERED,
      });
      return disabledConfig;
    }
    if (!isBrowserPhoneChannelEnabled()) {
      const reason = isDesktopApp() ? "DESKTOP_CALLING_DISABLED" : "WEB_CALLING_DISABLED";
      const disabledConfig = disabledBrowserPhoneConfig(reason);
      configRef.current = disabledConfig;
      dispatch({ type: BrowserPhoneAction.SET_CONFIG, payload: disabledConfig });
      dispatch({ type: BrowserPhoneAction.SET_ERROR, payload: null });
      dispatch({
        type: BrowserPhoneAction.SET_STATE,
        payload: BrowserPhoneState.REGISTERED,
      });
      return disabledConfig;
    }

    if (configRef.current) {
      return configRef.current;
    }
    if (loadConfigPromiseRef.current) {
      return loadConfigPromiseRef.current;
    }

    loadConfigPromiseRef.current = (async () => {
      try {
        const config = await getBrowserPhoneConfig();
        configRef.current = config;
        dispatch({ type: BrowserPhoneAction.SET_CONFIG, payload: config });
        if (!config?.enableBrowserPhone) {
          dispatch({ type: BrowserPhoneAction.SET_ERROR, payload: null });
          dispatch({
            type: BrowserPhoneAction.SET_STATE,
            payload: BrowserPhoneState.REGISTERED,
          });
        }
        return config;
      } catch (error) {
        dispatch({
          type: BrowserPhoneAction.SET_ERROR,
          payload: error?.message || "Unable to load phone config",
        });
        dispatch({
          type: BrowserPhoneAction.SET_STATE,
          payload: BrowserPhoneState.REGISTRATION_FAILED,
        });
        return null;
      } finally {
        loadConfigPromiseRef.current = null;
      }
    })();

    return loadConfigPromiseRef.current;
  }, [invalidateConfigCache]);

  const uploadRecordingFromUcm = useCallback(async (callLogId, providerCallId) => {
    const config = configRef.current;
    if (!config?.recordingFetchUrlTemplate || !callLogId || !providerCallId) {
      return;
    }
    try {
      const blob = await fetchGrandstreamRecording(config, providerCallId);
      if (blob) {
        await uploadCallRecording(callLogId, blob, providerCallId);
      }
    } catch {
      // Recording upload is best-effort; call log remains without playback until retry.
    }
  }, []);

  const buildSipHandlers = useCallback(() => ({
    remoteAudio: remoteAudioRef.current,
    onRegistered: () =>
      dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.REGISTERED }),
    onIncomingCall: (invitation) => {
      answeredRef.current = false;
      hangupReasonRef.current = "completed";
      failureDetailRef.current = null;
      callStartRef.current = Date.now();
      const phoneNumber = extractCaller(invitation);
      const sipCallId = extractSipCallId(invitation);
      callMetaRef.current = {
        phoneNumber,
        direction: "INBOUND",
        callLogId: null,
        accountNo: null,
        sipCallId,
        remoteIdentity: invitation.remoteIdentity,
      };
      dispatch({
        type: BrowserPhoneAction.SET_CALL,
        payload: {
          phoneNumber,
          direction: "INBOUND",
          remoteIdentity: invitation.remoteIdentity,
          sipCallId,
        },
      });
      dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.INCOMING_CALL });
      startIncomingCallAlert(phoneNumber);
      toast.info(`${phoneNumber || "Unknown caller"}`, {
        toastId: "browser-phone-incoming",
        autoClose: false,
      });
      notifyIncomingRinging(phoneNumber, sipCallId);
    },
    onCallProgress: ({ direction, phoneNumber, callId }) => {
      if (direction === "INBOUND") {
        return;
      }
      if (callId) {
        callMetaRef.current = {
          ...callMetaRef.current,
          sipCallId: String(callId),
        };
      }
      dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.OUTGOING_CALL });
      notifyCallStarted({
        callLogId: callMetaRef.current.callLogId,
        accountNo: callMetaRef.current.accountNo,
        phoneNumber,
        direction,
        status: "RINGING",
        sipCallId: callMetaRef.current.sipCallId,
      }).catch(() => undefined);
    },
    onCallEstablished: ({ direction, phoneNumber, callId }) => {
      stopIncomingCallAlert();
      answeredRef.current = true;
      callStartRef.current = Date.now();
      if (callId) {
        callMetaRef.current = {
          ...callMetaRef.current,
          sipCallId: String(callId),
        };
      }
      dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.ACTIVE_CALL });
      notifyCallStarted({
        callLogId: callMetaRef.current.callLogId,
        accountNo: callMetaRef.current.accountNo,
        phoneNumber,
        direction,
        status: "ANSWERED",
        sipCallId: callMetaRef.current.sipCallId,
      })
        .then((response) => recordCallLogResponse(response, phoneNumber))
        .catch(() => undefined);
    },
    onCallTerminated: ({ direction, phoneNumber, session, terminateReason }) => {
      if (
        terminateReason &&
        /^(reject|rejected|decline|declined)$/i.test(String(terminateReason))
      ) {
        hangupReasonRef.current = "reject";
      }
      stopIncomingCallAlert();
      toast.dismiss("browser-phone-incoming");
      const startedAt = callStartRef.current || Date.now();
      const durationSec = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
      const reason = hangupReasonRef.current || "completed";
      const callerLabel = phoneNumber || callMetaRef.current.phoneNumber || "Unknown caller";
      hangupReasonRef.current = "completed";
      const nextState = sipClientRef.current
        ? BrowserPhoneState.REGISTERED
        : BrowserPhoneState.ENDED;
      dispatch({ type: BrowserPhoneAction.SET_STATE, payload: nextState });
      dispatch({ type: BrowserPhoneAction.SET_CALL, payload: null });
      dispatch({ type: BrowserPhoneAction.SET_MUTED, payload: false });
      dispatch({ type: BrowserPhoneAction.SET_HOLD, payload: false });

      const payload = {
        callLogId: callMetaRef.current.callLogId,
        accountNo: callMetaRef.current.accountNo,
        phoneNumber: phoneNumber || callMetaRef.current.phoneNumber,
        direction,
        sipCallId: callMetaRef.current.sipCallId,
      };

      const finalizeCallLog = (endedResponse) => {
        const callLogId = extractCallLogId(endedResponse) || payload.callLogId;
        const providerCallId = payload.sipCallId;
        if (reason !== "reject" && reason !== "failed" && answeredRef.current && callLogId) {
          const syncAndRefresh = () =>
            syncCallRecording(callLogId)
              .catch(() => undefined)
              .finally(() => refreshHistory().catch(() => undefined));
          if (configRef.current?.recordingFetchUrlTemplate && providerCallId) {
            uploadRecordingFromUcm(callLogId, providerCallId).finally(syncAndRefresh);
          } else {
            syncAndRefresh();
          }
          return;
        }
        refreshHistory().catch(() => undefined);
      };

      if (reason === "reject") {
        notifyCallEnded({ ...payload, status: "REJECTED", durationSec: 0 })
          .then(finalizeCallLog)
          .catch(() => undefined);
      } else if (direction === "INBOUND") {
        const inboundStatus = resolveInboundTerminateStatus({
          answered: answeredRef.current,
          reason,
          durationSec,
        });
        if (inboundStatus === "FAILED") {
          const failureReason = resolveInboundFailureReason({
            answered: answeredRef.current,
            failureDetail: failureDetailRef.current,
            session,
            reason,
            durationSec,
          });
          if (
            !answeredRef.current &&
            (failureReason === "REMOTE_BYE" || failureReason === "CALLER_HANGUP")
          ) {
            toast.warn(`Missed call from ${callerLabel}`, { autoClose: 8000 });
          } else {
            toast.error(`Inbound call failed (${failureReason})`, { autoClose: 8000 });
          }
          notifyCallFailed({
            ...payload,
            failureReason,
          })
            .then(finalizeCallLog)
            .catch(() => undefined);
        } else if (inboundStatus === "NO_ANSWER") {
          toast.warn(`Missed call from ${callerLabel}`, { autoClose: 8000 });
          notifyCallEnded({
            ...payload,
            status: "NO_ANSWER",
            durationSec: 0,
            failureReason: failureDetailRef.current || undefined,
          })
            .then(finalizeCallLog)
            .catch(() => undefined);
        } else {
          notifyCallEnded({ ...payload, status: "COMPLETED", durationSec })
            .then(finalizeCallLog)
            .catch(() => undefined);
        }
      } else if (reason === "failed") {
        notifyCallFailed({
          ...payload,
          failureReason: failureDetailRef.current || "CALL_FAILED",
        })
          .then(finalizeCallLog)
          .catch(() => undefined);
      } else {
        notifyCallEnded({ ...payload, status: "COMPLETED", durationSec })
          .then(finalizeCallLog)
          .catch(() => undefined);
      }

      callMetaRef.current = {};
      callStartRef.current = null;
      answeredRef.current = false;
      failureDetailRef.current = null;
    },
    onAckTimeout: () => {
      hangupReasonRef.current = "failed";
      failureDetailRef.current = "SIP_ACK_TIMEOUT";
    },
    onMediaFailure: ({ iceState }) => {
      if (
        !answeredRef.current &&
        (phoneStateRef.current === BrowserPhoneState.INCOMING_CALL ||
          phoneStateRef.current === BrowserPhoneState.INCOMING_CONNECTING ||
          phoneStateRef.current === BrowserPhoneState.OUTGOING_CALL)
      ) {
        return;
      }
      hangupReasonRef.current = "failed";
      failureDetailRef.current = iceState ? `ICE_${String(iceState).toUpperCase()}` : "ICE_FAILED";
    },
    onRemoteBye: () => {
      if (hangupReasonRef.current === "completed") {
        failureDetailRef.current = answeredRef.current ? "REMOTE_BYE" : "CALLER_HANGUP";
      }
    },
    onIncomingCancel: () => {
      if (hangupReasonRef.current === "completed") {
        failureDetailRef.current = "SIP_CANCEL";
      }
    },
    onIncomingProgressFailed: ({ error }) => {
      const message = String(error?.message || "");
      if (/invalid status:\s*(5|6|9)/i.test(message)) {
        return;
      }
      hangupReasonRef.current = "failed";
      failureDetailRef.current = message || "INCOMING_PROGRESS_FAILED";
    },
    onMediaConnected: ({ iceState }) => {
      registerBrowserEvent({
        type: "MEDIA_CONNECTED",
        failureReason: iceState,
        at: new Date().toISOString(),
      }).catch(() => undefined);
    },
  }), [notifyIncomingRinging, recordCallLogResponse, refreshHistory, uploadRecordingFromUcm]);

  useEffect(() => {
    if (!sipClientRef.current) {
      return undefined;
    }
    sipClientRef.current.updateHandlers({
      ...buildSipHandlers(),
      remoteAudio: remoteAudioRef.current,
    });
    return undefined;
  }, [buildSipHandlers]);

  const connect = useCallback(async () => {
    if (!isBrowserPhoneChannelEnabled()) {
      return;
    }
    if (connectPromiseRef.current) {
      return connectPromiseRef.current;
    }

    connectPromiseRef.current = (async () => {
      const config = configRef.current || (await loadConfig());
      if (!config) {
        const message = "Unable to load telephony configuration.";
        dispatch({ type: BrowserPhoneAction.SET_ERROR, payload: message });
        dispatch({
          type: BrowserPhoneAction.SET_STATE,
          payload: BrowserPhoneState.REGISTRATION_FAILED,
        });
        throw new Error(message);
      }
      if (!config?.enableBrowserPhone) {
        return;
      }

      dispatch({ type: BrowserPhoneAction.SET_ERROR, payload: null });
      dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.CONNECTING });

      if (sipClientRef.current) {
        if (isActiveCallState(phoneStateRef.current)) {
          return;
        }
        if (phoneStateRef.current !== BrowserPhoneState.REGISTRATION_FAILED) {
          try {
            if (typeof sipClientRef.current.ensureReady === "function") {
              await sipClientRef.current.ensureReady();
            }
            dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.REGISTERED });
            return;
          } catch {
            await sipClientRef.current.disconnect().catch(() => undefined);
            sipClientRef.current = null;
          }
        } else {
          await sipClientRef.current.disconnect().catch(() => undefined);
          sipClientRef.current = null;
        }
      }

      try {
        sipClientRef.current = createSipClient(config, {
        ...buildSipHandlers(),
        onTransportError: (error) => {
          releaseSipClient().catch(() => undefined);
          dispatch({
            type: BrowserPhoneAction.SET_ERROR,
            payload: error?.message || (isDesktopApp() ? "SIP registration failed" : "WebSocket disconnected"),
          });
          dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.REGISTRATION_FAILED });
        },
      });
        await sipClientRef.current.connect();
        if (remoteAudioRef.current) {
          sipClientRef.current.updateHandlers({ remoteAudio: remoteAudioRef.current });
        }
        dispatch({ type: BrowserPhoneAction.SET_ERROR, payload: null });
        dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.REGISTERED });
        await registerBrowserEvent({ type: "REGISTERED", at: new Date().toISOString() });
      } catch (error) {
        await releaseSipClient();
        dispatch({ type: BrowserPhoneAction.SET_ERROR, payload: error?.message || "Registration failed" });
        dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.REGISTRATION_FAILED });
        await registerBrowserEvent({
          type: "REGISTER_FAILED",
          failureReason: error?.message || "UNKNOWN",
          at: new Date().toISOString(),
        });
        throw error;
      }
    })();

    try {
      await connectPromiseRef.current;
    } finally {
      connectPromiseRef.current = null;
    }
  }, [buildSipHandlers, loadConfig, releaseSipClient]);

  const disconnect = useCallback(async () => {
    await releaseSipClient();
    dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.DISCONNECTED });
    dispatch({ type: BrowserPhoneAction.SET_CALL, payload: null });
  }, [releaseSipClient]);

  const ensurePhoneReady = useCallback(async () => {
    if (!sipClientRef.current) {
      await connect();
    }
    if (typeof sipClientRef.current?.ensureReady === "function") {
      await sipClientRef.current.ensureReady();
    }
  }, [connect]);

  const startCall = useCallback(async (phoneNumber, options = {}) => {
    if (!sipClientRef.current) {
      throw new Error("Browser phone is not connected.");
    }

    await ensurePhoneReady();

    if (
      phoneStateRef.current === BrowserPhoneState.INCOMING_CALL ||
      phoneStateRef.current === BrowserPhoneState.INCOMING_CONNECTING
    ) {
      throw new Error("Reject the incoming call before placing an outbound call.");
    }

    if (
      phoneStateRef.current === BrowserPhoneState.OUTGOING_CALL ||
      phoneStateRef.current === BrowserPhoneState.ACTIVE_CALL ||
      phoneStateRef.current === BrowserPhoneState.HOLD
    ) {
      throw new Error("A call is already in progress. Hang up or tap Reset phone first.");
    }

    answeredRef.current = false;
    callStartRef.current = Date.now();
    callMetaRef.current = {
      phoneNumber,
      direction: "OUTBOUND",
      callLogId: options.callLogId || null,
      accountNo: options.accountNo || null,
      sipCallId: null,
    };

    dispatch({
      type: BrowserPhoneAction.SET_CALL,
      payload: {
        phoneNumber,
        direction: "OUTBOUND",
        callLogId: options.callLogId || null,
        startedAt: new Date().toISOString(),
      },
    });
    dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.OUTGOING_CALL });

    try {
      await sipClientRef.current.dial(phoneNumber);
    } catch (error) {
      if (isSdkSuccessResult(error)) {
        return;
      }
      hangupReasonRef.current = "failed";
      dispatch({
        type: BrowserPhoneAction.SET_STATE,
        payload: sipClientRef.current ? BrowserPhoneState.REGISTERED : BrowserPhoneState.FAILED,
      });
      dispatch({ type: BrowserPhoneAction.SET_CALL, payload: null });
      try {
        await notifyCallFailed({
          callLogId: options.callLogId,
          accountNo: options.accountNo,
          phoneNumber,
          direction: "OUTBOUND",
          failureReason: error?.message || "DIAL_FAILED",
        });
      } catch {
        // Best-effort telemetry; dial failure is already surfaced to the user.
      }
      throw error;
    }
  }, [ensurePhoneReady]);

  const acceptCall = useCallback(async () => {
    if (!sipClientRef.current) return;
    stopIncomingCallAlert();
    dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.INCOMING_CONNECTING });
    try {
      await sipClientRef.current.acceptCall();
    } catch (error) {
      const message = String(error?.message || "");
      if (/invalid status:\s*(5|6|9)/i.test(message)) {
        dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.ACTIVE_CALL });
        await registerBrowserEvent({ type: "CALL_ACCEPTED", at: new Date().toISOString() });
        return;
      }
      hangupReasonRef.current = "failed";
      failureDetailRef.current = message || "ACCEPT_FAILED";
      dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.INCOMING_CALL });
      throw error;
    }
    await registerBrowserEvent({ type: "CALL_ACCEPTED", at: new Date().toISOString() });
  }, []);

  const rejectCall = useCallback(async () => {
    hangupReasonRef.current = "reject";
    stopIncomingCallAlert();
    if (sipClientRef.current) {
      await sipClientRef.current.rejectCall();
    }
  }, []);

  const endCall = useCallback(async () => {
    hangupReasonRef.current = "completed";
    if (sipClientRef.current) {
      await sipClientRef.current.hangup();
    }
  }, []);

  const resetCall = useCallback(async () => {
    stopIncomingCallAlert();
    toast.dismiss("browser-phone-incoming");
    hangupReasonRef.current = "completed";
    failureDetailRef.current = null;
    callMetaRef.current = {};
    callStartRef.current = null;
    answeredRef.current = false;
    if (sipClientRef.current) {
      await sipClientRef.current.forceEndSession().catch(() => undefined);
      if (configRef.current?.providerName === "D2C_CLOUD_PBX") {
        try {
          await sipClientRef.current.ensureReady();
        } catch {
          await sipClientRef.current.disconnect().catch(() => undefined);
          sipClientRef.current = null;
          await connect().catch(() => undefined);
        }
      }
    }
    dispatch({ type: BrowserPhoneAction.SET_STATE, payload: BrowserPhoneState.REGISTERED });
    dispatch({ type: BrowserPhoneAction.SET_CALL, payload: null });
    dispatch({ type: BrowserPhoneAction.SET_MUTED, payload: false });
    dispatch({ type: BrowserPhoneAction.SET_HOLD, payload: false });
    toast.info("Phone reset — you can dial again.", { autoClose: 3000 });
  }, [connect]);

  const setMute = useCallback((muted) => {
    const next = Boolean(muted);
    sipClientRef.current?.setMuted(next);
    dispatch({ type: BrowserPhoneAction.SET_MUTED, payload: next });
  }, []);

  const setHold = useCallback(async (hold) => {
    const next = Boolean(hold);
    if (sipClientRef.current) {
      await sipClientRef.current.setHold(next);
    }
    dispatch({ type: BrowserPhoneAction.SET_HOLD, payload: next });
    dispatch({
      type: BrowserPhoneAction.SET_STATE,
      payload: next ? BrowserPhoneState.HOLD : BrowserPhoneState.ACTIVE_CALL,
    });
  }, []);

  useEffect(() => {
    if (store.state !== BrowserPhoneState.CONNECTING) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (phoneStateRef.current !== BrowserPhoneState.CONNECTING) {
        return;
      }
      if (sipClientRef.current || connectPromiseRef.current) {
        return;
      }
      dispatch({
        type: BrowserPhoneAction.SET_ERROR,
        payload: "Phone connection timed out. Verify your extension and SIP password, then tap Reset phone.",
      });
      dispatch({
        type: BrowserPhoneAction.SET_STATE,
        payload: BrowserPhoneState.REGISTRATION_FAILED,
      });
    }, 15000);

    return () => window.clearTimeout(timer);
  }, [store.state]);

  useEffect(() => {
    if (!isAuthenticated()) return undefined;
    refreshHistory().catch(() => undefined);
    return () => {
      releaseSipClient().catch(() => undefined);
    };
  }, [refreshHistory, releaseSipClient]);

  useEffect(() => {
    if (!isDesktopApp()) {
      return undefined;
    }
    return subscribeDesktopIncomingPopupActions({
      onRejected: () => {
        hangupReasonRef.current = "reject";
      },
    });
  }, []);

  const actions = useMemo(
    () => ({
      connect,
      disconnect,
      startCall,
      acceptCall,
      rejectCall,
      endCall,
      resetCall,
      setMute,
      setHold,
      refreshHistory,
      loadConfig,
      invalidateConfigCache,
      setRemoteAudioElement,
      ensurePhoneReady,
    }),
    [
      connect,
      disconnect,
      endCall,
      resetCall,
      loadConfig,
      invalidateConfigCache,
      refreshHistory,
      rejectCall,
      setHold,
      setMute,
      setRemoteAudioElement,
      startCall,
      acceptCall,
      ensurePhoneReady,
    ]
  );

  return { store, actions };
};
